import { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { useStore } from 'statux'

import DashboardSettingsMaterialsInputs
    from '../DashboardSettingsMaterialsInputs/DashboardSettingsMaterialsInputs'

import Alert from '../../../../components/Alert/Alert'
import Form from '../../../../components/Form/Form'
import InputPassword from '../../../../components/InputPassword/InputPassword'

import authProvider from '../../../../utils/auth-provider'
import { createGUID } from '../../../../utils/strings'
import { fetchClient } from '../../../../utils/fetch'
import { graphqlClient, gql } from '../../../../utils/gql'

export default function DashboardSettingsForm (props = {
    id: null,
    profile: null,
    setProfile: null,
    successText: null,
    changePassword: false,
    customValidations: null,
    materials: false
}) {
    const history = useHistory()

    const id = props.id || `settings-${createGUID()}`

    const alertId = `${id}-msg`

    const successText = props.successText || 'Saved successfully'

    const failText = props.changePassword
        ? 'Your password hasn\'t been updated'
        : 'Your settings haven\'t been saved.'

    const [submissionState, setSubmissionState] = useState({
        success: null,
        message: null
    })

    const { profile, setProfile } = props

    const [user, setUser] = useStore('user')

    const onSubmit = async (e) => {
        e.target.focus()

        const formData = new window.FormData(e.target)

        const postData = Array.from(formData.entries())
            .filter(arr => arr[0]
                && arr[1] !== (profile || {})[arr[0]])
            .reduce((acc, curr) => {
                let value = curr[1]

                if (props.materials)
                    try {
                        value = JSON.parse(value)
                    }
                    catch (e) {
                        console.warn(e)
                    }

                acc[`${curr[0]}`] = value

                return acc
            }, {})

        if (postData.email
                && postData.email === user.email)
            delete postData.email

        if (!Object.keys(postData).length)
            return null

        if (postData.email) {
            const resAuth = await fetchClient
                .post('/auth/change-email', {
                    json: {
                        new_email: postData.email
                    },
                    headers: {
                        'x-hasura-role': 'me'
                    }
                })
                .catch(err => err)

            if (!resAuth.status
                    || resAuth.status !== 204)
                console.warn(resAuth)

            if (!profile)
                delete postData.email
        }

        const displayName = postData.display_name
            || `${postData.first_name || profile.first_name} ${
                postData.last_name || profile.last_name}`

        const isDisplayNameChange = ('first_name' in postData)
            || ('last_name' in postData)
            || user.display_name !== displayName

        const avatarUrl = postData.avatar_url

        const isAvatarUrlChange = ('avatar_url' in postData)
            && user.avatar_url !== avatarUrl

        const usersMutation = (isDisplayNameChange
                || isAvatarUrlChange)
            && gql`update_users (
                where: { id: { _eq: "${user.id}" }},
                _set: {
                    display_name: "${displayName}"
                    ${isAvatarUrlChange ? `, avatar_url: "${avatarUrl}"` : ''}
                }
            ) {
                returning {
                    display_name
                    avatar_url
                }
            }`

        delete postData.avatar_url

        delete postData.display_name

        const mutationSetStr = gql`{${Object.keys(postData)
            .map(key => `${key}: ${key === 'company_materials'
                ? `$${key}`
                : `"${postData[key]}"`}`)
            .join(',')}}`

        const profilesMutation = Object.keys(postData).length
            && gql`update_profiles (
                where: { id: { _eq: "${profile.id}" }},
                _set: ${mutationSetStr}
            ) {
                returning {
                    ${Object.keys(postData).map(key => `${key}`).join('\n')}
                }
            }`

        if (!usersMutation && !profilesMutation)
            return

        const mutationVariables = postData.company_materials
            && { company_materials: postData.company_materials }

        const mutation = await graphqlClient
            .requestSafe(gql`
                mutation (
                    $company_materials: jsonb
                ) {
                    ${usersMutation || ''}
                    ${profilesMutation || ''}
                }
            `, mutationVariables, {
                headers: {
                    'x-hasura-role': 'me'
                }
            })

        const { data, errors } = mutation

        const isSuccess = Boolean(Object.keys(data || {}).length)

        if (!isSuccess)
            console.warn(errors)

        if (isSuccess
                && ((data.update_profiles || {}).returning || []).length) {
            const profileData = Object.assign(profile,
                data.update_profiles.returning[0])

            setProfile(profileData)

            history.replace(
                `${history.location.pathname}${history.location.search}`,
                Object.assign(history.location.state || {}, {
                    profile: profileData
                }))
        }

        const isEmailChange = isSuccess
            && 'email' in (((data.update_profiles || {})
                .returning || [])[0] || {})

        if (isEmailChange
                || (isSuccess
                    && ((data.update_users || {}).returning || []).length)) {
            const userObj = Object.assign(JSON.parse(JSON.stringify(user)),
                ((data.update_users || {}).returning || [])[0] || {})

            const userSession = authProvider.getSession()
            userSession.user = userObj

            if (isEmailChange)
                userSession.user.email
                    = data.update_profiles.returning[0].email

            authProvider.setSession(userSession)
            setUser(userSession.user)
        }

        setSubmissionState({
            success: isSuccess,
            message: isSuccess
                ? successText
                : (mutation || {}).statusText || failText
        })

        return isSuccess
    }

    const onChangePasswordSubmit = async (e) => {
        const formData = new window.FormData(e.target)

        const data = Array.from(formData.entries())
            .reduce((acc, curr) => {
                acc[`${curr[0]}`] = curr[1]
                return acc
            }, {})

        if (data.new_password !== data.confirm_password)
            return console.warn('Password mismatch error')

        delete data.confirm_password
        delete data.username

        const res = await fetchClient
            .post('/auth/change-password', {
                json: data
            })
            .catch(err => err) || {}

        const isSuccess = res.status === 204

        // Edge-case fail
        if (!isSuccess)
            console.warn(res)

        let message = isSuccess
            ? successText
            : (res || {}).statusText || failText

        if (message === 'Unauthorized')
            message = 'Password incorrect.'

        setSubmissionState({
            success: isSuccess,
            message: message
        })

        return isSuccess
    }

    useEffect(() => {
        if (submissionState.success === null)
            return

        const form = document.getElementById(id)

        if (form)
            Array.from(form.querySelectorAll('[type=submit]'))
                .forEach(el => (el.disabled = true))

        const inputs = submissionState.success
                && form
            ? Array.from(form.querySelectorAll('input, textarea, select'))
            : []

        // Reset on success
        inputs
            .forEach(el => {
                el.parentElement.classList.remove('dirty')
                el.classList.remove('dirty')

                if (props.changePassword)
                    el.value = ''
            })

        // Reset submission state on re-focus
        const onFormFocus = (e) => {
            if (!e.target.tagName.match(/(input|textarea|select)/i)
                    || submissionState.success === null)
                return

            form.removeEventListener('focusin', onFormFocus)

            setSubmissionState({
                success: null,
                message: null
            })
        }

        if (form)
            form.addEventListener('focusin', onFormFocus)

        const alert = document.getElementById(alertId)

        if (!alert
                || alert.style.display === 'none')
            return

        alert.focus()
    }, [submissionState.success])

    const renderChangePasswordInputs = () => (
        <>
            <input
                className="display-none"
                type="email"
                name="username"
                autoComplete="username"
                value={user.email}
                readOnly
            />
            <div className="form-field-floating margin-3-b">
                <InputPassword
                    id={`${id}-old-password`}
                    name="old_password"
                    type="password"
                    autoComplete="off"
                    autoCapitalize="none"
                    spellCheck="false"
                    minLength="8"
                    placeholder=" "
                    required
                />
                <label
                    htmlFor={`${id}-old-password`}
                >
                    Current password
                </label>
                <p
                    id={`${id}-old-password-invalid`}
                    role="alert"
                >
                    A password with at least 8 characters is required
                </p>
            </div>
            <div className="form-field-floating margin-3-b">
                <InputPassword
                    id={`${id}-new-password`}
                    name="new_password"
                    type="password"
                    autoComplete="new-password"
                    autoCapitalize="none"
                    spellCheck="false"
                    minLength="8"
                    placeholder=" "
                    required
                />
                <label
                    htmlFor={`${id}-new-password`}
                >
                    New password
                </label>
                <p
                    id={`${id}-new-password-invalid`}
                    role="alert"
                >
                    A password with at least 8 characters is required
                </p>
            </div>
            <div className="form-field-floating margin-3-b">
                <InputPassword
                    id={`${id}-confirm-password`}
                    name="confirm_password"
                    type="password"
                    autoComplete="new-password"
                    autoCapitalize="none"
                    spellCheck="false"
                    minLength="8"
                    placeholder=" "
                    required
                />
                <label
                    htmlFor={`${id}-confirm-password`}
                >
                    Confirm new password
                </label>
                <p
                    id={`${id}-confirm-password-invalid`}
                    role="alert"
                >
                    A password with at least 8 characters is required
                </p>
            </div>
        </>)

    return (
        <Form
            id={id}
            onSubmit={props.changePassword
                ? onChangePasswordSubmit
                : onSubmit}
            customValidations={(props.customValidations || [])
                .concat(props.changePassword
                    ? [{
                        id: `${id}-new-password`,
                        message: 'Your new password can\'t match your current password',
                        valid: (e) => {
                            const oldPassword = document
                                .getElementById(`${id}-old-password`)

                            return Boolean((e || {}).target
                                && oldPassword
                                && e.target.value !== oldPassword.value)
                        }
                    }, {
                        id: `${id}-confirm-password`,
                        message: 'Your new password and confirmation password don\'t match',
                        valid: (e) => {
                            const newPassword = document
                                .getElementById(`${id}-new-password`)

                            return Boolean((e || {}).target
                                && newPassword
                                && e.target.value === newPassword.value)
                        }
                    }]
                    : [])}
            enableOnChange
            tabIndex="-1"
        >
            {props.materials
                ? (
                    <DashboardSettingsMaterialsInputs
                        defaultValues={(profile || {}).company_materials}
                        {...props}
                    />)
                : (props.changePassword
                    ? renderChangePasswordInputs()
                    : props.children)}
            <div className="row-condensed">
                <div className="col-auto">
                    <button
                        className="btn-secondary btn-lg"
                        type="submit"
                        disabled
                    >
                        Save
                    </button>
                </div>
                <div className="col col-12-sm-down">
                    <Alert
                        id={`${alertId}`}
                        className="margin-0-t margin-2-t-sm-down"
                        role={submissionState.success
                            ? 'status'
                            : 'alert'}
                        type={submissionState.success
                            ? 'success'
                            : 'error'}
                        dismissible={false}
                        fluidCondensed
                        icon
                        style={{
                            minHeight: '50px',
                            display: submissionState.success !== null
                                ? 'inline-flex'
                                : 'none',
                            padding: '5px 0'
                        }}
                        tabIndex="-1"
                    >
                        <p style={{ lineHeight: 1.44 }}>
                            {submissionState.message}
                            {!submissionState.success
                                && (
                                    <>
                                        <br />
                                        Please try again.
                                    </>)}
                        </p>
                    </Alert>
                </div>
            </div>
        </Form>
    )
}
